import {Avatar, Button, message} from 'antd'
import {
    course_getCourseSubjectsByCourseStudent_Api,
    course_getGraduationReview_Api,
    course_getStudentSubjectHistory_Api,
    course_grantCertificateToStudentInCourse_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {FaUserGraduate} from 'react-icons/fa6'
import {GoDotFill} from 'react-icons/go'
import {
    PiCertificate,
    PiCheckCircleFill,
    PiClockCountdown,
    PiListBullets,
    PiWarningCircle,
    PiXCircle,
} from 'react-icons/pi'
import {TbHistory} from 'react-icons/tb'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import LoadingBlock from 'views/Loading/LoadingBlock'
import classNames from 'classnames'
import {useLocation} from "react-use";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import getAliasName from "../../../../helpers/getAliasName";
import copyToClipboard from "copy-to-clipboard";
import {IoCopyOutline} from "react-icons/io5";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import getTitle from "../../../../helpers/getTitle";
import {formatSimplifyDate} from "../../../../helpers/dateUtils";

// ─── Shared helpers ───────────────────────────────────────────────────────────

const ResultStatusBadge = ({status, size = 'sm'}) => {
    const t = useTranslate()
    const lg = size === 'lg'
    if (status === 1) {
        return (
            <span className={classNames(
                'inline-flex items-center gap-1 rounded-full font-semibold',
                lg ? 'px-3 py-1 text-sm bg-green-100 text-green-700 border border-green-300'
                   : 'px-2 py-0.5 text-xs bg-green-100 text-green-700'
            )}>
                <PiCheckCircleFill size={lg ? 15 : 11}/>
                {t('passed')}
            </span>
        )
    }
    if (status === 0) {
        return (
            <span className={classNames(
                'inline-flex items-center gap-1 rounded-full font-semibold',
                lg ? 'px-3 py-1 text-sm bg-red-100 text-red-700 border border-red-300'
                   : 'px-2 py-0.5 text-xs bg-red-100 text-red-600'
            )}>
                <PiXCircle size={lg ? 15 : 11}/>
                {t('failed')}
            </span>
        )
    }
    return (
        <span className={classNames(
            'inline-flex items-center gap-1 rounded-full font-semibold',
            lg ? 'px-3 py-1 text-sm bg-amber-50 text-amber-700 border border-amber-300'
               : 'px-2 py-0.5 text-xs bg-gray-100 text-color-400'
        )}>
            <PiClockCountdown size={lg ? 15 : 11}/>
            {t('pending')}
        </span>
    )
}

const PassFailBadge = ({passed}) => {
    const t = useTranslate()
    if (passed === null || passed === undefined) return null
    return passed === 1
        ? <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-md">{t('passed')}</span>
        : <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-md">{t('not passed')}</span>
}

// ─── Subject History Drawer ───────────────────────────────────────────────────

const SubjectHistoryContent = ({course_id, student_id, subject_id}) => {
    const t = useTranslate()
    const [items, setItems] = useState(null)

    const {handleAsyncAction: fetchHistory, isLoading} = useAsyncAction({
        apiInfo: course_getStudentSubjectHistory_Api,
        onSuccess: (result, data) => {
            setItems(_.get(data, 'response.data.data', []))
        },
    })

    React.useEffect(() => {
        if (course_id && student_id && subject_id) {
            fetchHistory({}, {course_id, student_id, subject_id})
        }
    }, [course_id, student_id, subject_id])

    if (isLoading || items === null) return <LoadingBlock/>

    if (items.length === 0) {
        return (
            <div className="p-6 text-center text-sm text-color-400 italic">{t('no data')}</div>
        )
    }

    return (
        <div className="px-4 pt-3 pb-6 flex flex-col">
            {items.map((item, i) => {
                const status = item.result_status ?? -1
                const isLast = i === items.length - 1
                const scoreVal = item.score_number ?? item.score_text
                const courseClass = item.course_class
                const subject = item.subject

                const dotBg = status === 1 ? 'bg-green-500'
                    : status === 0 ? 'bg-red-400'
                        : 'bg-amber-400'
                const cardBorder = status === 1 ? 'border-green-200'
                    : status === 0 ? 'border-red-200'
                        : 'border-amber-200'
                const cardHeaderBg = status === 1 ? 'bg-green-50'
                    : status === 0 ? 'bg-red-50'
                        : 'bg-amber-50'

                return (
                    <div key={item.id ?? i} className="flex gap-3">
                        {/* Timeline column */}
                        <div className="flex flex-col items-center flex-shrink-0 w-6">
                            <div className={classNames(
                                'p-1 flex flex-center flex-shrink-0 rounded-lg',
                                dotBg
                            )}>
                                {status === 1 && <PiCheckCircleFill size={17} className="text-white"/>}
                                {status === 0 && <PiXCircle size={17} className="text-white"/>}
                                {status !== 1 && status !== 0 && <PiClockCountdown size={17} className="text-white"/>}
                            </div>
                            {!isLast && <div className="flex-1 w-1 bg-color-100 my-1"/>}
                        </div>

                        {/* Content */}
                        <div className={classNames('flex-1 min-w-0', isLast ? 'mb-0' : 'mb-4')}>
                            {/* Date */}
                            {item.result_date && (
                                <div className="mb-1.5 font-mono font-semibold text-base text-slate-600 tracking-wide">
                                    {formatSimplifyDate(item.result_date, 'full')}
                                </div>
                            )}

                            {/* Card */}
                            <div className={classNames('rounded-xl border overflow-hidden', cardBorder)}>
                                {/* Status header */}
                                <div className={classNames('px-3 py-2 flex items-center justify-between gap-2 rounded-t-xl border-b', cardHeaderBg, cardBorder)}>
                                    <ResultStatusBadge status={status} size="lg"/>
                                    {item.eligibility_status === 1 && (
                                        <span className="text-xs text-green-600 flex items-center gap-1">
                                            <PiCheckCircleFill size={11}/>
                                            {t('eligible')}
                                        </span>
                                    )}
                                </div>
                                {/* Details */}
                                <div className="p-3 flex flex-col gap-2 text-xs">
                                    {courseClass?.title && (
                                        <div className="flex items-center gap-2">
                                            <span className="w-14 text-color-400 tracking-wider flex-shrink-0">{t('class')}:</span>
                                            <span className="font-bold text-slate-600 tracking-wide truncate">{courseClass.title}</span>
                                        </div>
                                    )}
                                    {subject?.title && (
                                        <div className="flex items-center gap-2">
                                            <span className="w-14 text-color-400 tracking-wider flex-shrink-0">{t('subject')}:</span>
                                            <span className="font-medium text-color-100 truncate">{subject.title}</span>
                                        </div>
                                    )}
                                    {scoreVal != null && (
                                        <div className="flex items-center gap-2">
                                            <span className="w-14 text-color-400 tracking-wider flex-shrink-0">{t('score')}:</span>
                                            <span className="font-bold text-lg text-color-000 leading-none">{scoreVal}</span>
                                        </div>
                                    )}
                                    {item.notes && (
                                        <div className="flex items-start gap-2">
                                            <span className="w-14 text-color-400 tracking-wider flex-shrink-0">{t('notes')}:</span>
                                            <span className="text-color-300 italic">{item.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// ─── Subject Card (in Course Progress Drawer) ─────────────────────────────────

const SubjectCard = ({item, onViewHistory}) => {
    const t = useTranslate()
    const [showAttendance, setShowAttendance] = useState(false)
    const [showGrade, setShowGrade] = useState(false)

    const subjectTitle = item?.subject?.title ?? item?.subject_title ?? '—'
    const className = item?.course_class?.title
    const resultStatus = item?.result_status ?? -1
    const eligibilityStatus = item?.eligibility_status
    const totalSessions = item?.total_sessions ?? 0
    const numSessions = item?.number_of_sessions
    const passAttPct = item?.passed_attendance_percentage
    const passedAttStatus = item?.passed_attendance_status
    const avgScore = item?.avg_score
    const passedScore = item?.passed_score
    const passedScoreStatus = item?.passed_score_status
    const gradeDetails = item?.grade_details ?? []
    const attendanceDetails = item?.attendance_details ?? []

    const ccPercent = numSessions > 0 ? Math.round((totalSessions / numSessions) * 100) : null

    let borderColor, bgColor;
    switch (resultStatus) {
        case 1:
            borderColor = 'border-green-500';
            bgColor = 'bg-green-50';
            break;
        case 0:
            borderColor = 'border-primary-500';
            bgColor = 'bg-primary-50';
            break;
        default:
            borderColor = 'border-amber-400';
            bgColor = 'bg-amber-50';
    }

    return (
        <div className="flex flex-col shadow-sm hover:shadow-md rounded-2xl overflow-hidden cursor-pointer">
            {/* Subject header */}
            <div className={classNames("px-4 py-3 flex items-center justify-between gap-3 border-b", bgColor, borderColor)}>
                <div className={`border-l-6 h-12 rounded-full ${borderColor}`}/>
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <span className="font-bold text-base text-color-000 tracking-wide">
                        {subjectTitle}
                    </span>
                    {className && (
                        <span className="text-color-400 tracking-wider">
                            {t('class')}: <span className="font-medium text-slate-600">{className}</span>
                        </span>
                    )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                    <ResultStatusBadge status={resultStatus} size="lg"/>
                    <button
                        onClick={() => onViewHistory(item)}
                        style={{ padding: '0.25rem 0.5rem' }}
                        className="flex items-center gap-1 text-xs text-color-300 hover:text-secondary-600 rounded-md hover:bg-secondary-50 transition-all duration-300">
                        <TbHistory size={13}/>
                        {t('history')}
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="p-4 flex flex-col gap-3">
                {/* Eligibility */}
                <div className="flex items-center gap-2 tracking-wider">
                    <span className="font-semibold text-xs text-slate-500 flex-shrink-0">
                        {`${t('eligibility')}: `}
                    </span>
                    {eligibilityStatus === 1 ? (
                        <span className="font-semibold text-green-600 flex items-center gap-1">
                            <PiCheckCircleFill size={17}/> {t('eligible')}
                        </span>
                    ) : (
                        <span className="font-medium text-amber-600 flex items-center gap-1">
                            <PiWarningCircle size={17}/> {t('not eligible')}
                        </span>
                    )}
                </div>

                {/* Attendance */}
                {numSessions != null && (
                    <div className="pt-3 border-t border-color-50">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex flex-col">
                                <span className="font-semibold text-slate-700 tracking-wider">
                                    {t('attendance')}
                                </span>
                                <div style={{height: 3}} className="mt-1 mb-2 w-20 rounded-full bg-gray-400"/>
                            </div>
                            <PassFailBadge passed={passedAttStatus}/>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-color-300 flex-wrap mb-2">
                            <span className="text-color-400 tracking-wider">
                                {t('number sessions')}: <span className="font-semibold text-color-000">{totalSessions}/{numSessions} {t('sessions')}</span>
                                {ccPercent != null && <span className="ml-1 font-medium text-color-300 italic">({ccPercent}%)</span>}
                            </span>
                            {passAttPct != null && (
                                <span className="text-color-400 tracking-wider">
                                    {t('threshold')}: <span className="font-semibold text-color-100">{passAttPct}%</span>
                                </span>
                            )}
                        </div>
                        {/* Progress bar with threshold marker */}
                        {numSessions > 0 && (
                            <div className="relative h-2 bg-gray-200 rounded-full mb-2" style={{overflow: 'visible'}}>
                                <div
                                    className={classNames(
                                        'h-full rounded-full',
                                        passedAttStatus === 1 ? 'bg-green-500'
                                            : passedAttStatus === 0 ? 'bg-red-400'
                                            : 'bg-amber-400'
                                    )}
                                    style={{width: `${Math.min(ccPercent ?? 0, 100)}%`}}
                                />
                                {passAttPct != null && (
                                    <div
                                        className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-gray-500 rounded"
                                        style={{left: `${Math.min(passAttPct, 100)}%`}}
                                    />
                                )}
                            </div>
                        )}
                        {/* See more: attendance detail records */}
                        {attendanceDetails.length > 0 && (
                            <div className="mt-2 flex flex-col gap-2">
                                <div className="flex justify-end">
                                    <button
                                        onClick={() => setShowAttendance(v => !v)}
                                        className="font-medium text-xs text-color-300 hover:text-secondary-600 tracking-wider">
                                        {showAttendance ? t('hide') : t('view detail')}
                                    </button>
                                </div>
                                {showAttendance && (
                                    <div className="mt-1 flex flex-col border border-color-100 rounded-lg divide-y divide-gray-200">
                                        {attendanceDetails.map((ad, i) => (
                                            <div key={ad.id ?? i} className="p-2 flex items-center justify-between gap-2 text-xs">
                                                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                                    <span className="font-semibold text-slate-700 tracking-wide">
                                                        {ad.attendance?.title ?? `${t('session')} ${i + 1}`}
                                                    </span>
                                                    {ad.attendance?.attendance_date && (
                                                        <span className="text-color-400 tracking-wider">
                                                            {formatSimplifyDate(ad.attendance.attendance_date, 'medium')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    {ad.status === 1
                                                        ? <span className="font-medium text-green-600">{t('present')}</span>
                                                        : <span className="font-medium text-red-500">{t('absent')}</span>
                                                    }
                                                    <span className="text-color-300 italic tracking-wider">
                                                        {ad.number_of_sessions} {t('sessions')}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Score */}
                <div className="pt-3 border-t border-color-50">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-col">
                            <span className="font-semibold text-slate-700 tracking-wider">
                                {t('score')}
                            </span>
                            <div style={{height: 3}} className="mt-1 mb-2 w-20 rounded-full bg-gray-400"/>
                        </div>
                        <PassFailBadge passed={passedScoreStatus}/>
                    </div>
                    <div className="flex items-baseline gap-3 text-xs text-color-300 flex-wrap">
                        {avgScore != null ? (
                            <span className="text-color-400 tracking-wider">
                                {t('average')}: <span className="font-bold text-sm text-color-000">{avgScore}</span>
                            </span>
                        ) : (
                            <span className="italic text-color-400">{t('not graded yet')}</span>
                        )}
                        {passedScore != null && (
                            <span className="text-color-400 tracking-wider">
                                {t('threshold')}: <span className="font-medium text-color-200">{passedScore}</span>
                            </span>
                        )}
                    </div>
                    {/* Grade breakdown */}
                    {gradeDetails.length > 0 && (
                        <div className="mt-2 flex flex-col gap-2">
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setShowGrade(v => !v)}
                                    className="font-medium text-xs text-color-300 hover:text-secondary-600 tracking-wider">
                                    {showGrade ? t('hide') : t('view detail')}
                                </button>
                            </div>
                            {showGrade && (
                                <div className="mt-1 flex flex-col border border-color-100 rounded-lg divide-y divide-gray-200">
                                    {gradeDetails.map((g, gi) => (
                                        <div key={gi} className="p-2 flex items-start justify-between gap-2 text-xs">
                                            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                            <span className="font-semibold text-slate-700 tracking-wide">
                                                {g.grade?.title ?? '—'}
                                            </span>
                                                {g.final_grade_date && (
                                                    <span className="text-color-400 tracking-wider">
                                                    {formatSimplifyDate(g.final_grade_date, 'medium')}
                                                </span>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            <span className="font-semibold text-color-000">
                                                {g.final_score_number ?? g.score_number ?? g.score_text ?? '—'}
                                            </span>
                                                <span className="text-color-300 italic tracking-wider">
                                                {t('score_weight')} ×{g.score_weight}
                                            </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

// ─── Course Progress Drawer content ──────────────────────────────────────────

const CourseProgressContent = ({course_id, student, onViewHistory}) => {
    const t = useTranslate()
    const [subjects, setSubjects] = useState(null)

    const student_id = getId(student)

    const {handleAsyncAction: fetchSubjects, isLoading} = useAsyncAction({
        apiInfo: course_getCourseSubjectsByCourseStudent_Api,
        onSuccess: (result, data) => {
            setSubjects(_.get(data, 'response.data.data', []))
        },
    })

    React.useEffect(() => {
        if (course_id && student_id) {
            fetchSubjects({}, {course_id, student_id})
        }
    }, [course_id, student_id])

    if (isLoading || subjects === null) return <LoadingBlock/>

    if (subjects.length === 0) {
        return (
            <div className="p-6 text-center text-sm text-color-400 italic">{t('no data')}</div>
        )
    }

    const order = {1: 0, 0: 1, null: 2, '-1': 2}
    const sorted = [...subjects].sort((a, b) => {
        const sa = order[String(a.result_status)] ?? 2
        const sb = order[String(b.result_status)] ?? 2
        return sa - sb
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap1">
                <span className="font-bold text-base text-slate-700 tracking-wider">
                    {t('current progress')}
                </span>
                <div className="mt-0.5 w-20 h-1 rounded-full bg-gray-400"/>
            </div>
            {sorted.map((item, i) => (
                <SubjectCard
                    key={item.id ?? item.subject_id ?? i}
                    item={item}
                    onViewHistory={onViewHistory}
                />
            ))}
            <div className="h-12"/>
        </div>
    )
}

// ─── Main list row ────────────────────────────────────────────────────────────

const EligibilityCell = ({item}) => {
    const t = useTranslate()
    const eligible = item?.eligible
    const missing = item?.missing_subject_titles ?? []

    if (eligible) {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                <PiCheckCircleFill size={12}/>
                {t('eligible')}
            </span>
        )
    }
    if (missing.length > 0) {
        const shown = missing.slice(0, 2)
        const extra = missing.length - shown.length
        return (
            <span className="text-xs text-amber-700">
                {t('missing')}: <span className="font-medium">{shown.join(', ')}</span>
                {extra > 0 && <span className="text-color-400"> (+{extra})</span>}
            </span>
        )
    }
    return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
            <PiWarningCircle size={12}/>
            {t('not eligible')}
        </span>
    )
}

const GraduationStudentRow = ({item, onViewProgress}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const student = item?.student ?? {}
    const completed = item?.completed_subjects ?? 0
    const required = item?.required_subjects ?? 0
    const hasMedal = item?.has_medal

    const progressText = `${completed} / ${required}`

    const medalCell = hasMedal
        ? <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full"><PiCertificate size={11}/>{t('awarded')}</span>
        : <span className="text-xs text-color-400">—</span>

    const viewBtn = (
        <button
            onClick={() => onViewProgress(item)}
            className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 transition-colors">
            <PiListBullets size={13}/>
            <span className="hidden md:inline">{t('view progress')}</span>
        </button>
    )

    return (
        <div className="border-b last:border-b-0 hover:background-50">
            {isLg ? (
                <div
                    className="grid items-center px-3 py-2 gap-3 text-sm"
                    style={{gridTemplateColumns: '2fr 64px 1fr 110px 100px'}}>
                    <div className="flex items-center gap-3">
                        <Avatar
                            size={36}
                            src={student?.avatar || student?.user?.avatar}
                            icon={<FaUserGraduate size={16} className="text-color-500"/>}
                            className="flex flex-center background-200"
                        />
                        <div className="flex flex-col min-w-0">
                            <span className="font-bold text-color-000 truncate">
                                {renderIf(
                                    student?.patron_saint_name,
                                    <span className="mr-1 font-medium text-sm text-slate-600">
                                        {getAliasName(student, 'patron_saint')}
                                    </span>
                                )}
                                {student.fullname}
                            </span>
                            {renderIf(
                                student?.student_idcode,
                                <span
                                    onClick={() => {
                                        copyToClipboard(student?.student_idcode)
                                        message.info(t('copied'))
                                    }}
                                    className="font-semibold text-xs text-blue-500 hover:text-blue-700 tracking-wider cursor-pointer">
                                    #{student?.student_idcode}
                                    <IoCopyOutline size={10} className="ml-1"/>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="font-semibold text-color-000 text-center">{progressText}</div>
                    <div className="min-w-0"><EligibilityCell item={item}/></div>
                    <div>{medalCell}</div>
                    <div className="flex justify-end">{viewBtn}</div>
                </div>
            ) : (
                <div className="px-3 py-2.5 flex items-center justify-between gap-3">
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                        <span className="font-semibold text-sm text-color-000 truncate">{student.fullname}</span>
                        <div className="flex items-center gap-2 flex-wrap">
                            {student.student_idcode && (
                                <span className="text-xs font-mono text-blue-500">{`#${student.student_idcode}`}</span>
                            )}
                            <span className="text-xs text-color-400">{progressText}</span>
                            <EligibilityCell item={item}/>
                            {medalCell}
                        </div>
                    </div>
                    {viewBtn}
                </div>
            )}
        </div>
    )
}

const TableHeader = () => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    if (!isLg) return null
    return (
        <div
            className="grid px-3 py-2 background-100 border-b border-color-100 text-xs font-semibold text-color-300 uppercase tracking-wider gap-3"
            style={{gridTemplateColumns: '2fr 64px 1fr 110px 100px'}}>
            <span>{t('student')}</span>
            <span className="text-center">{t('progress')}</span>
            <span>{t('eligibility')}</span>
            <span>{t('certificate')}</span>
            <span/>
        </div>
    )
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export const CourseGraduationDashboard = ({course_id, can_edit = false}) => {
    const t = useTranslate()
    const location = useLocation()

    const [progressStudent, setProgressStudent] = useState(null)
    const [historySubject, setHistorySubject] = useState(null)

    const onError = notifyOnError(t)
    const refresh_deps = useMemo(
        () => [
            course_id,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [course_id]
    )
    const {handleAsyncAction: grantAll, isLoading: granting} = useAsyncAction({
        apiInfo: course_grantCertificateToStudentInCourse_Api,
        onError,
        onSuccess: () => successNotify(NotificationActionTypes.SUCCESS, t),
    })

    const confirmGrantAll = useCallback(() => {
        showDeleteConfirmDialog({
            translate: t,
            title: 'grant certificate to all students',
            message: 'are you sure you want to grant certificate to all eligible students',
            onOk: () => grantAll({}, {id: course_id}),
        })
    }, [course_id, grantAll])

    const [pager_widget] = usePaginationHelper({
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: course_getGraduationReview_Api,
            query: bindQueryParams([{id: course_id}]),
            renderItem: (item, index) => (
                <GraduationStudentRow
                    key={index}
                    item={item}
                    onViewProgress={setProgressStudent}
                />
            ),
            renderWrap: (child, _, count) => count > 0 && (
                <div className="border border-color-100 rounded-lg overflow-hidden">
                    <TableHeader/>
                    <div className="flex flex-col">{child}</div>
                </div>
            ),
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: {keyword: 'keyword'},
        dependencies: refresh_deps,
        withKey: createPagerKey(refresh_deps),
    })

    const {student, registered_classes = [], registered_subjects = []} = progressStudent ?? {}

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-col flex-1">
                    <div className="flex items-center gap-2">
                        <PiCertificate size={20} className="text-indigo-500"/>
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">{t('graduation review')}</h2>
                    </div>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <p className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('review student progress and manage certificate awards for this course')}
                    </p>
                </div>
                {can_edit && (
                    <Button
                        color="primary"
                        variant="solid"
                        loading={granting}
                        className="px-5 py-2 font-semibold rounded-lg"
                        onClick={confirmGrantAll}>
                        <PiCertificate size={14}/>
                        {t('grant certificate to all')}
                    </Button>
                )}
            </div>

            {pager_widget}

            {/* Course Progress Drawer */}
            {progressStudent && (
                <FullSideModalLayout
                    right={true}
                    title={
                        <div className="font-bold text-color-000 tracking-wide">
                            {t('subject progress')}
                        </div>
                    }
                    onCancel={() => {
                        setProgressStudent(null)
                        setHistorySubject(null)
                    }}>
                    <div className="px-3 flex flex-col gap-4">
                        <div className="p-3 border border-color-50 shadow-md rounded-xl flex flex-col">
                            <span className="font-semibold text-slate-700 tracking-wider">
                                {t('student information')}
                            </span>
                            <div style={{height: 3}} className="mt-1 mb-3 w-20 rounded-full bg-gray-400"/>
                            <div className="flex items-center gap-3">
                                <Avatar
                                    size={40}
                                    src={student?.avatar || student?.user?.avatar}
                                    icon={<FaUserGraduate size={20} className="text-color-500"/>}
                                    className="flex flex-center background-200"
                                />
                                <div className="flex flex-col gap-0.5">
                                    <div className="flex items-baseline gap-1 leading-snug">
                                        {renderIf(
                                            student?.patron_saint_name,
                                            <span className="font-medium text-sm text-slate-600">
                                                {getAliasName(student, 'patron_saint')}
                                            </span>
                                        )}
                                        <span className="font-bold text-color-000">
                                            {student?.fullname}
                                        </span>
                                    </div>
                                    {renderIf(
                                        student?.student_idcode,
                                        <span
                                            onClick={() => {
                                                copyToClipboard(student?.student_idcode)
                                                message.info(t('copied'))
                                            }}
                                            className="font-semibold text-xs text-blue-500 hover:text-blue-700 tracking-wider cursor-pointer">
                                            #{student?.student_idcode}
                                            <IoCopyOutline size={10} className="ml-1"/>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <CourseProgressContent
                            course_id={course_id}
                            student={progressStudent?.student}
                            onViewHistory={(subjectItem) => {
                                setHistorySubject({
                                    subject_id: subjectItem.subject_id ?? subjectItem?.subject?.id,
                                    subject_title: subjectItem.subject_title ?? subjectItem.subject?.title,
                                    student_id: getId(progressStudent?.student),
                                })
                            }}
                        />
                    </div>
                </FullSideModalLayout>
            )}

            {/* Subject History Drawer */}
            {historySubject && (
                <FullSideModalLayout
                    onCancel={() => setHistorySubject(null)}
                    right={true}
                    title={
                        <div className="mt-2 flex items-center gap-2">
                            <div className="p-2 flex flex-center bg-secondary-50 rounded-lg">
                                <TbHistory size={15} className="text-indigo-500"/>
                            </div>
                            <div className="flex flex-col flex-1 min-w-0">
                                <div className="font-bold text-color-000 tracking-wide">
                                    {t('history')}
                                </div>
                                {historySubject.subject_title && (
                                    <span className="font-medium text-xs text-secondary-600 tracking-wider">
                                        <GoDotFill size={8} className="mr-1 text-secondary-400"/>
                                        {historySubject.subject_title}
                                    </span>
                                )}
                            </div>
                        </div>
                    }>
                    <SubjectHistoryContent
                        course_id={course_id}
                        student_id={historySubject.student_id}
                        subject_id={historySubject.subject_id}
                    />
                </FullSideModalLayout>
            )}
        </div>
    )
}

export default CourseGraduationDashboard
